import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { useAudit } from "@/components/landing/AuditContext";

export const FinalCTA = () => {
  const openDemo = useDemo();
  const openAudit = useAudit();

  const goToAudit = (e) => {
    e.preventDefault();
    openAudit();
  };
  return (
    <section id="final-cta" data-testid="final-cta-section" className="bg-gradient-to-b from-white via-slate-50/60 to-slate-100/60 py-16 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-[#0a0a14] px-8 py-16 text-center sm:px-16 sm:py-24">
          {/* layered ambient glow */}
          <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-violet-600/30 blur-[120px]" />
          <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-600/25 blur-[120px]" />
          {/* grid overlay */}
          <div className="pointer-events-none absolute inset-0 bg-grid opacity-30" />
          {/* fine border glow */}
          <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Ready when you are
            </span>

            {/* THE PUNCHLINE — moved from hero */}
            <h2 className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-6xl lg:text-7xl">
              Run your clinic.{" "}
              <span className="relative inline-block whitespace-nowrap">
                <span className="text-white/40">Not your</span>{" "}
                <span className="relative inline-block text-rose-400">
                  tools
                  <motion.span
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.35, ease: "easeOut" }}
                    className="absolute inset-x-0 top-1/2 h-[4px] origin-left -translate-y-1/2 rounded-full bg-rose-400 sm:h-[5px]"
                    style={{ transformOrigin: "left" }}
                    aria-hidden="true"
                  />
                </span>
                <span className="text-white/40">.</span>
              </span>
            </h2>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Replace the messy stack with one calm OS. Fill the schedule, engage every
              patient, keep them coming back — without the tab-switching tax.
            </p>

            {/* Audit-aware nudge — works from every page */}
            <a
              href="/#clinic-audit"
              data-testid="final-audit-link"
              onClick={goToAudit}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/75 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-violet-400/50 hover:bg-white/10 hover:text-white"
            >
              <Sparkles className="h-3 w-3 text-violet-300" />
              Haven&apos;t taken the audit? · 60 seconds
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </a>

            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/build"
                data-testid="final-build-cta"
                className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-8 text-sm font-bold text-white shadow-lg shadow-violet-600/40 ring-1 ring-inset ring-white/10 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-violet-600/50 sm:w-auto"
              >
                Build Your OS <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <button
                data-testid="final-book-demo"
                onClick={openDemo}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-8 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 sm:w-auto"
              >
                <CalendarCheck className="h-4 w-4" /> Book a Demo
              </button>
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/55">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                HIPAA-ready
              </span>
              <span className="h-1 w-1 rounded-full bg-white/20" />
              <span>SOC 2 aligned</span>
              <span className="h-1 w-1 rounded-full bg-white/20" />
              <span>99.9% uptime</span>
              <span className="h-1 w-1 rounded-full bg-white/20" />
              <span>2-way EMR sync</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
